/**
 * DocumentEditor rationale:
 * - Small custom hooks keep effect dependencies narrow; reduces accidental rerenders.
 * - Two-layer navigation protection (router prompt + popstate) covers browser back edge cases.
 * - Dirty tracking uses initial snapshots instead of abstractions; keeps mental model simple.
 * - Success messages auto-clear so feedback always feels current, not stale.
 * - Revision IDs fetched separately so revision issues never block document load/edit.
 * - Title updates are immediate for perceived responsiveness.
 * - Token validity checked early to avoid chasing auth errors deep in fetch chains.
 */

import "../../index.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useEffect, useState, useRef } from "react";
import Header from "../layout/Header";
import EditorMenu from "./EditorMenu";
import DocumentBody from "./DocumentBody";
import NavigationConfirmModal from "../modals/NavigationConfirmModal";
import DocumentNameInput from "./DocumentNameInput";
import DeleteConfirmModal from "../modals/DeleteConfirmModal";
import ExportDocxModal from "../modals/ExportDocxModal";
import usePrompt from "./usePrompt";
import PromptBox from "./PromptBox";
import RevisionModal from "../modals/RevisionModal";
import CoachReviewModal from "../modals/CoachReviewModal";
import {
  loadDocument,
  handleSave,
  handleFormat,
  handleAddBits,
  handleCoachReview,
  handleDelete,
  handleExportDocx,
  isMobileDevice,
} from "./documentUtils";
import { DEFAULT_WORDS_PER_MINUTE, parseHeaderLine } from "./documentBodyUtils";
import { fetchSettings } from "../layout/settingsUtils";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

function isTokenValid(auth) {
  const expiresAt = auth.user?.expires_at;
  if (!auth.user || !auth.user.access_token || !expiresAt) return false;
  return Date.now() / 1000 < expiresAt;
}

function useCursorWait(isWaiting) {
  useEffect(() => {
    if (isWaiting) {
      document.body.classList.add("body-cursor-wait");
    } else {
      document.body.classList.remove("body-cursor-wait");
    }
    return () => document.body.classList.remove("body-cursor-wait");
  }, [isWaiting]);
}

// Loads each document id once. The OIDC library replaces auth.user every
// time it silently renews the token (roughly hourly, and on returning to
// an idle tab), so keying the load on the user object would re-fetch the
// document and wipe any unsaved edits. Only the id matters: loadedIdRef
// records what's open, and is also set when a draft's first save moves
// the URL to its new id.
function useLoadDocument(auth, effectiveId, loadedIdRef, setField) {
  const signedIn = Boolean(auth.user);
  useEffect(() => {
    if (!signedIn || loadedIdRef.current === effectiveId) return;
    loadedIdRef.current = effectiveId;
    loadDocument({ paramId: effectiveId, auth, setField });
  }, [effectiveId, signedIn]); // eslint-disable-line react-hooks/exhaustive-deps
}

function useRevisionIds(documentState, auth, setRevisions) {
  useEffect(() => {
    if (documentState.loading || documentState.isNew) return;
    async function fetchRevisions() {
      if (!documentState.id) return;
      try {
        const res = await fetch(`${API_BASE_URL}/documents/${documentState.id}`, {
          headers: { Authorization: `Bearer ${auth.user?.access_token}` },
        });
        if (res.ok) {
          const doc = await res.json();
          setRevisions(doc.revIds || []);
        }
      } catch {
        setRevisions([]);
      }
    }
    fetchRevisions();
  }, [documentState.id, documentState.loading, documentState.isNew, auth.user, setRevisions]);
}

function useSyncInitialValues(documentState, setField) {
  useEffect(() => {
    if (!documentState.loading) {
      setField("initialName", documentState.name);
      setField("initialBody", documentState.body);
    }
  }, [documentState.id, documentState.loading]); // eslint-disable-line react-hooks/exhaustive-deps
}

function useApplyProvidedDefaultName(documentState, defaultName, setDocumentState) {
  useEffect(() => {
    if (!documentState.loading &&
        defaultName &&
        documentState.name === "New Set") {
      setDocumentState(prev => ({
        ...prev,
        name: defaultName,
        initialName: defaultName,
      }));
    }
  }, [documentState.loading, documentState.name, defaultName, setDocumentState]);
}

function useResetInitialValuesOnSuccess(success, documentState, setField) {
  useEffect(() => {
    if (success) {
      setField("initialName", documentState.name);
      setField("initialBody", documentState.body);
    }
  }, [success]); // eslint-disable-line react-hooks/exhaustive-deps
}

function useAutoClearSuccess(success, setField) {
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setField("success", ""), 2000);
    return () => clearTimeout(t);
  }, [success, setField]);
}

function useBeforeUnload(dirty) {
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (dirty) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [dirty]);
}

// Always confirm before leaving with unsaved edits - even with an expired
// token, where the edits are still worth copying out or saving after the
// session is refreshed.
function useDirtyNavigationProtection(dirty, setField) {
  usePrompt(dirty, (tx) => {
    setField("showNavModal", true);
    setField("navTx", tx);
  });

  useEffect(() => {
    if (!dirty) return;
    window.history.pushState({ isDocumentEditor: true }, "");
    let blocked = false;
    const handlePopState = () => {
      if (dirty && !blocked) {
        blocked = true;
        setField("showNavModal", true);
        setField("navTx", null);
        window.history.pushState({ isDocumentEditor: true }, "");
        setTimeout(() => { blocked = false; }, 100);
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [dirty, setField]);
}

function useDocumentTitle(name) {
  useEffect(() => {
    document.title = name ? `${name} - JokeLab` : "JokeLab";
  }, [name]);
}

// Loads the user's words-per-minute and AI-disabled settings.
// Kept in state (not refetched per render) so they can also be updated
// immediately when the user saves new values from the header dropdown.
function useEditorSettings(auth) {
  const [wordsPerMinute, setWordsPerMinute] = useState(DEFAULT_WORDS_PER_MINUTE);
  const [aiDisabled, setAiDisabled] = useState(false);

  useEffect(() => {
    if (!auth.user) return;
    fetchSettings(auth.user.access_token)
      .then((s) => {
        setWordsPerMinute(s.words_per_minute);
        setAiDisabled(Boolean(s.ai_disabled));
      })
      .catch(() => {});
  }, [auth.user]);

  return [wordsPerMinute, setWordsPerMinute, aiDisabled, setAiDisabled];
}

async function selectRevision({ revId, auth, revisions, setRevisionDoc, setShowRevision }) {
  try {
    const res = await fetch(`${API_BASE_URL}/documents/${revId}`, {
      headers: { Authorization: `Bearer ${auth.user?.access_token}` },
    });
    if (!res.ok) throw new Error("Failed");
    const doc = await res.json();
    const revIndex = revisions.indexOf(revId);
    const revNumber = revIndex !== -1 ? revIndex + 1 : null;
    setRevisionDoc({ ...doc, revNumber });
    setShowRevision(true);
  } catch {
    setRevisionDoc(null);
    setShowRevision(false);
  }
}

/* -------------------- Component -------------------- */

function DocumentEditor({ showHeader = true, documentType, id: passedId, defaultName }) {
  const auth = useAuth();
  const navigate = useNavigate();
  const { id: paramId } = useParams?.() || {};
  useLocation();
  const effectiveId = passedId ?? paramId ?? null;

  const [documentState, setDocumentState] = useState({
    mode: isMobileDevice() ? "perform" : "edit",
    id: effectiveId,
    isNew: false, // an unsaved draft; created on the server by its first save
    name: "",
    body: "",
    saving: false,
    error: "",
    success: "",
    loading: true,
    confirmDelete: false,
    showNavModal: false,
    formatting: false,
    reviewing: false,
    coachReview: null,
    showCoachReview: false,
    initialName: "",
    initialBody: "",
    navTx: null,
  });
  const loadedIdRef = useRef(undefined);
  const pendingNavigation = useRef(null);

  const [revisions, setRevisions] = useState([]);
  const [showRevision, setShowRevision] = useState(false);
  const [revisionDoc, setRevisionDoc] = useState(null);
  const [promptHistory, setPromptHistory] = useState([]);
  const [showExportDocx, setShowExportDocx] = useState(false);
  const [rawView, setRawView] = useState(false);
  const wysiwygRef = useRef(null);
  const [activeLineIdx, setActiveLineIdx] = useState(null);

  const setField = (field, value) =>
    setDocumentState(prev => ({ ...prev, [field]: value }));

  useCursorWait(documentState.formatting || documentState.reviewing);
  useLoadDocument(auth, effectiveId, loadedIdRef, setField);
  useRevisionIds(documentState, auth, setRevisions);
  useSyncInitialValues(documentState, setField);
  useApplyProvidedDefaultName(documentState, defaultName, setDocumentState);
  useResetInitialValuesOnSuccess(documentState.success, documentState, setField);
  useAutoClearSuccess(documentState.success, setField);
  useBeforeUnload(documentState.name !== documentState.initialName ||
                   documentState.body !== documentState.initialBody);
  useDirtyNavigationProtection(
    documentState.name !== documentState.initialName ||
    documentState.body !== documentState.initialBody,
    setField
  );
  useDocumentTitle(documentState.name);
  const [wordsPerMinute, setWordsPerMinute, aiDisabled, setAiDisabled] =
    useEditorSettings(auth);

  const dirty =
    documentState.name !== documentState.initialName ||
    documentState.body !== documentState.initialBody;

  // Formatting toolbar state: shared between the toolbar under the menu
  // (rendered by DocumentBody, which owns the actual contentEditable
  // lines) and the matching items in the Edit menu below, both of which
  // trigger the same WysiwygBody actions via this one ref.
  const lineFocused = activeLineIdx != null;
  const activeRawLine = lineFocused ? documentState.body.split("\n")[activeLineIdx] : null;
  const isHeaderActive = activeRawLine != null && parseHeaderLine(activeRawLine) !== null;
  const handleApplyBold = () => wysiwygRef.current?.applyBold();
  const handleApplyBit = () => wysiwygRef.current?.applyBitHeader();
  const handleApplyStage = () => wysiwygRef.current?.applyStageDirection();

  const handleSelectRevision = (revId) =>
    selectRevision({
      revId,
      auth,
      revisions,
      setRevisionDoc,
      setShowRevision,
    });

  const handleCloseRevision = () => {
    setShowRevision(false);
    setRevisionDoc(null);
  };

  const documentContext = {
    id: documentState.id,
    isNew: documentState.isNew,
    onCreated: (newId) => {
      setDocumentState(prev => ({ ...prev, id: newId, isNew: false }));
      loadedIdRef.current = newId;
      // Give a new document from /document_editor its own URL, so a refresh
      // reopens it rather than starting another draft
      if (!passedId && !paramId) navigate(`/document_editor/${newId}`, { replace: true });
    },
    name: documentState.name,
    body: documentState.body,
    auth,
    navigate,
    setSaving: (v) => setField("saving", v),
    setError: (v) => setField("error", v),
    setSuccess: (v) => setField("success", v),
    setBody: (v) => setField("body", v),
    setFormatting: (v) => setField("formatting", v),
    setReviewing: (v) => setField("reviewing", v),
    setCoachReview: (v) => setField("coachReview", v),
    setShowCoachReview: (v) => setField("showCoachReview", v),
    setConfirmDelete: (v) => setField("confirmDelete", v),
    documentType,
    wordsPerMinute,
  };

  const fetchRevisions = async () => {
    if (!documentState.id) return;
    try {
      const res = await fetch(`${API_BASE_URL}/documents/${documentState.id}`, {
        headers: { Authorization: `Bearer ${auth.user?.access_token}` },
      });
      if (res.ok) {
        const doc = await res.json();
        setRevisions(doc.revIds || []);
      }
    } catch {
      setRevisions([]);
    }
  };

  // Renews an expired session in the background (e.g. after the tab sat
  // idle past the token's lifetime). Resolves to the fresh user, or null if
  // the refresh token is also no longer valid.
  const refreshingSession = useRef(null);
  const refreshSession = () => {
    if (!refreshingSession.current) {
      refreshingSession.current = auth
        .signinSilent()
        .catch(() => null)
        .finally(() => {
          refreshingSession.current = null;
        });
    }
    return refreshingSession.current;
  };

  const handleSaveWithRevision = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    let ctx = documentContext;
    if (!isTokenValid(auth)) {
      const user = await refreshSession();
      if (!user) {
        setField(
          "error",
          "Your sign-in has expired. Your edits are still here - copy them, then sign in again to save."
        );
        return;
      }
      ctx = { ...documentContext, auth: { ...auth, user } };
    }
    await handleSave(e, ctx);
    await fetchRevisions();
  };

  // Never block editing (or navigate away) because the token expired: that
  // would throw away unsaved edits. Start renewing it so saving works.
  const handleEditAttempt = () => {
    if (!isTokenValid(auth)) refreshSession();
    return true;
  };

  const isEditingDisabled = documentState.formatting || documentState.reviewing;

  const handleNavCancel = () => {
    setField("showNavModal", false);
    setField("navTx", null);
  };
  const handleNavDiscard = () => {
    setField("showNavModal", false);
    if (documentState.navTx && documentState.navTx.retry) documentState.navTx.retry();
    setField("navTx", null);
  };

  if (documentState.loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: "#f7f2e3" }}
      >
        <div className="text-lg font-semibold">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen w-full flex flex-col items-center mt-0 pt-0${
        documentState.formatting ? " cursor-wait" : ""
      }`}
      style={{
        backgroundColor: "#e6dcc6",
        cursor: documentState.formatting ? "wait" : undefined,
      }}
    >
      <div
        className="w-full max-w-5xl flex flex-col"
        style={{
          backgroundColor: "#f7f2e3",
          minHeight: "100vh",
          // Practice mode scrolls inside its own teleprompter view, so cap the
          // page at the viewport rather than letting it grow with the script
          height: documentState.mode === "practice" ? "100vh" : undefined,
          padding: 0,
        }}
      >
        {showHeader && (
          <Header
            user={auth.user}
            logoSrc="/mast_branding_without_name.png"
            onSettingsChange={(s) => {
              setWordsPerMinute(s.words_per_minute);
              setAiDisabled(Boolean(s.ai_disabled));
            }}
            centerControl={
              <DocumentNameInput
                name={documentState.name}
                setName={(v) => setField("name", v)}
                dirty={dirty}
                onEditAttempt={handleEditAttempt}
              />
            }
          />
        )}
        <div className="flex-1 min-h-0 flex flex-col items-center w-full h-full">
          <div className="w-full h-full bg-white p-1 flex flex-col flex-1 min-h-0">
            <form
              onSubmit={handleSaveWithRevision}
              className="flex flex-col gap-1 flex-1 min-h-0 h-full"
            >
              <div className="flex items-center justify-between w-full">
                <EditorMenu
                  onSave={handleSaveWithRevision}
                  onDelete={() => setField("confirmDelete", true)}
                  onEdit={() => setField("mode", "edit")}
                  onPractice={() => setField("mode", "practice")}
                  onPerform={() => setField("mode", "perform")}
                  onFormat={() => handleFormat(documentContext)}
                  onAddBits={() => handleAddBits(documentContext)}
                  onCoachReview={() => handleCoachReview(documentContext)}
                  onExportDocx={() => setShowExportDocx(true)}
                  saving={documentState.saving}
                  setConfirmDelete={(v) => setField("confirmDelete", v)}
                  mode={documentState.mode}
                  formatting={documentState.formatting}
                  revisions={revisions}
                  onSelectRevision={handleSelectRevision}
                  aiDisabled={aiDisabled}
                  rawView={rawView}
                  onSetRawView={setRawView}
                  lineFocused={lineFocused}
                  isHeaderActive={isHeaderActive}
                  onApplyBold={handleApplyBold}
                  onApplyBit={handleApplyBit}
                  onApplyStage={handleApplyStage}
                />
                <div className="ml-4 text-xs text-gray-400 min-h-[1.25rem] text-right font-normal">
                  {documentState.formatting && "Formatting..."}
                  {documentState.reviewing && "Reviewing..."}
                  {!documentState.formatting && !documentState.reviewing && documentState.error && (
                    <span className="text-red-400">{documentState.error}</span>
                  )}
                  {!documentState.formatting &&
                    !documentState.reviewing &&
                    !documentState.error &&
                    documentState.success && (
                      <span className="text-gray-400">
                        {documentState.success}
                      </span>
                    )}
                </div>
              </div>
              <DocumentBody
                mode={documentState.mode}
                body={documentState.body}
                setBody={(v) => setField("body", v)}
                disabled={isEditingDisabled}
                onEditAttempt={handleEditAttempt}
                wordsPerMinute={wordsPerMinute}
                rawView={rawView}
                wysiwygRef={wysiwygRef}
                onActiveLineChange={setActiveLineIdx}
                lineFocused={lineFocused}
                isHeaderActive={isHeaderActive}
                onApplyBold={handleApplyBold}
                onApplyBit={handleApplyBit}
                onApplyStage={handleApplyStage}
              />
              {documentState.mode !== "practice" && !aiDisabled && (
                <PromptBox
                  documentText={documentState.body}
                  contextText={promptHistory.map((item) => item.prompt).join("\n")}
                  auth={auth}
                  promptHistory={promptHistory}
                  setPromptHistory={setPromptHistory}
                />
              )}
            </form>
            {documentState.id && documentState.confirmDelete && (
              <DeleteConfirmModal
                onConfirm={() => handleDelete(documentContext)}
                onCancel={() => setField("confirmDelete", false)}
              />
            )}
            <ExportDocxModal
              open={showExportDocx}
              onExport={({ includeHeaders, includeTiming }) => {
                setShowExportDocx(false);
                handleExportDocx(documentContext, { includeHeaders, includeTiming });
              }}
              onCancel={() => setShowExportDocx(false)}
            />
            {documentState.showNavModal && (
              <NavigationConfirmModal
                onCancel={handleNavCancel}
                onDiscard={handleNavDiscard}
              />
            )}
            <RevisionModal
              revisionDoc={revisionDoc}
              open={showRevision}
              onClose={handleCloseRevision}
              currentContent={documentState.body}
            />
            <CoachReviewModal
              open={documentState.showCoachReview}
              review={documentState.coachReview}
              onClose={() => setField("showCoachReview", false)}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentEditor;